import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  Signal,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router, ActivatedRoute } from "@angular/router";
import { FontAwesomeModule } from "@fortawesome/angular-fontawesome";

import { TranslateModule, TranslateService } from "@ngx-translate/core";

import {
  ILocalizationLanguage,
  RoutingService,
  PopUpDialogInformalService,
  PopUpDialogConfirmService,
  StateService,
  LanguageService,
  LoginService,
  BuildInfoService,
} from "@dert-services";

import { DesktopConnectionStatusComponent } from "./ui/connection-status/connection-status.component";
import { DesktopPersonInfoComponent } from "./ui/person-info/person-info.component";
import { DesktopNavigationComponent } from "./ui/navigation/navigation.component";
import { DesktopLanguageSelectComponent } from "./ui/language-select/language-select.component";
import { InfoPanelComponent } from "./ui/info-panel/info-panel.component";
import { InfoPanel } from "./ui/info-panel/info.panel.model";
import { PopUpInformalType } from "../ui-components/pop-ups/pop-up-informal/pop-up-informal.model";
import { BehaviorSubject, map, Observable, of } from "rxjs";
import { toSignal } from "@angular/core/rxjs-interop";
import { environment } from "src/environments/environment";

@Component({
  selector: "app-desktop-side-bar",
  templateUrl: "./side-bar.component.html",
  styleUrls: ["./side-bar.component.scss"],
  imports: [
    DesktopConnectionStatusComponent,
    DesktopPersonInfoComponent,
    DesktopNavigationComponent,
    DesktopLanguageSelectComponent,
    CommonModule,
    InfoPanelComponent,
    TranslateModule,
    FontAwesomeModule,
  ],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DesktopSideBarComponent implements OnInit {
  constructor(private languageService: LanguageService) {}

  readonly isProductionEnvironment = environment.production;

  readonly state = inject(StateService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly routingService = inject(RoutingService);
  private readonly popUpDialogConfirmService = inject(PopUpDialogConfirmService);
  private readonly popUpDialogInformalService = inject(PopUpDialogInformalService);
  private readonly loginService = inject(LoginService);
  private readonly buildInfoService = inject(BuildInfoService);

  readonly buildInfo = toSignal(this.buildInfoService.getBuildInfo());

  private readonly mainPage = "Dashboard";
  readonly showSelectedPage: Signal<boolean> = computed(
    () => this.routingService.pageName() !== this.mainPage
  );
  readonly currentPage: Signal<string> = computed(() => {
    return this.state.pageNameOverride() || this.routingService.pageName();
  });
  infos = signal<InfoPanel[]>([]);

  public roles = new BehaviorSubject<Array<string>>(this.loginService.roles());

  //Mock data for testing
  languages: ILocalizationLanguage[] = [
    { code: "en", label: "English" },
    { code: "sk", label: "Slovak" },
    { code: "cs", label: "Czech" },
    { code: "hu", label: "Hungarian" },
  ];

  public selectedLanguage: ILocalizationLanguage;

  ngOnInit() {
    const currLang = this.languageService.getCurrentLanguage();
    this.selectedLanguage = this.languages.find(
      (lang) => lang.code === currLang
    );
  }

  goBack() {
    let backRoute: string | undefined;
    let route = this.router.routerState.root;
    
    // Debug: log the route tree
    console.log('Current URL:', this.router.url);
    console.log('Root route:', route);
    
    const findBackRoute = (r: any): string | undefined => {
      if (r.snapshot && r.snapshot.data && r.snapshot.data['backRoute']) {
        console.log('Found backRoute:', r.snapshot.data['backRoute']);
        return r.snapshot.data['backRoute'];
      }
      if (r.firstChild) {
        return findBackRoute(r.firstChild);
      }
      return undefined;
    };
    
    backRoute = findBackRoute(route);
    console.log('Final backRoute:', backRoute);
    
    if (backRoute) {
      this.router.navigate([backRoute]);
    } else {
      this.router.navigate(["dashboard"]);
    }
  }

  goToDashboard(): void {
    void this.router.navigate(["/dashboard"]);
  }

  //For testing purposes only
  addInfoPanel() {
    this.infos.update((infos) => [
      ...infos,
      {
        title: "Proveďte hotovostní uzávěrku",
        text: "Nejpozději do konce dnešního dne je třeba provést hotovostní uzávěrku pokladny.",
      },
    ]);
  }

  //For testing purposes only
  createFakeTripAlert() {
    console.log("createFakeTripAlert");
    this.state.waitingTrip.set({ id: "#123123" });
  }

  async showMoreInfo(title: string, message: string, index: number) {
    await this.popUpDialogInformalService.confirm({
      title,
      message,
      type: PopUpInformalType.Info,
    });
    this.infos.update((currentItems) => {
      const newItems = [...currentItems];
      newItems.splice(index, 1);
      return newItems;
    });
  }

  public async onLanguageChange(event: ILocalizationLanguage) {
    await this.languageService.setLanguage(event.code, false);
  }
}
